import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Heading,
  VStack,
  Text,
  SimpleGrid,
  Spinner,
  Alert,
  AlertIcon,
  Button,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  HStack,
  IconButton,
  Tooltip
} from '@chakra-ui/react';
import { StrategyData, ApiResponse, TagsResponse } from '../types/strategy';
import StrategyCard from './StrategyCard';
import TagSelector from './TagSelector';

const Dashboard: React.FC = () => {
  const [strategies, setStrategies] = useState<StrategyData[]>([]);
  const [totalBalance, setTotalBalance] = useState<string>('0');
  const [previousDayBalance, setPreviousDayBalance] = useState<string>('0');
  const [balanceChange, setBalanceChange] = useState<string>('0');
  const [balanceChangePercentage, setBalanceChangePercentage] = useState<string>('0');
  const [pureBalanceChange, setPureBalanceChange] = useState<string>('0');
  const [pureBalanceChangePercentage, setPureBalanceChangePercentage] = useState<string>('0');
  const [totalFlows, setTotalFlows] = useState<string>('0');
  const [hasPreviousDayData, setHasPreviousDayData] = useState<boolean>(false);
  const [lastUpdated, setLastUpdated] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const [tags, setTags] = useState<string[]>([]);
  const [selectedTag, setSelectedTag] = useState<string | null>(null);
  const [tagsLoading, setTagsLoading] = useState<boolean>(false);

  // Helper function to find the closest data point to a target time
  const findClosestDataPoint = (historicalBalances: any[], targetTime: number, toleranceMinutes: number = 5) => {
    if (!historicalBalances || historicalBalances.length === 0) return null;

    const toleranceMs = toleranceMinutes * 60 * 1000;
    let closest = null;
    let minDiff = Infinity;

    for (const balance of historicalBalances) {
      const diff = Math.abs(balance.timestamp - targetTime);
      if (diff <= toleranceMs && diff < minDiff) {
        minDiff = diff;
        closest = balance;
      }
    }

    return closest;
  };

  // Helper function to get previous day at 8 AM UTC
  const getPreviousDay8AMUTC = () => {
    const now = new Date();
    const previousDay = new Date(now);
    previousDay.setUTCDate(now.getUTCDate() - 1);
    previousDay.setUTCHours(8, 0, 0, 0);
    return previousDay.getTime();
  };

  // Helper function to calculate APY from daily change
  const calculateAPY = (dailyChange: string, previousBalance: string): string => {
    const change = parseFloat(dailyChange);
    const balance = parseFloat(previousBalance);

    if (balance <= 0) return '0.00';

    // Calculate daily rate: (change / previous_balance)
    const dailyRate = change / balance;

    // Convert to APY: (1 + daily_rate)^365 - 1
    const apy = (Math.pow(1 + dailyRate, 365) - 1) * 100;

    return apy.toFixed(2);
  };

  const fetchTags = async () => {
    try {
      setTagsLoading(true);
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/tags`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data: TagsResponse = await response.json();
      setTags(data.tags);
    } catch (err) {
      console.error('Error fetching tags:', err);
      // Don't set error state for tags as it's not critical
    } finally {
      setTagsLoading(false);
    }
  };

  const fetchStrategies = async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      // Build URL with optional tag parameter
      let url = `${process.env.REACT_APP_API_BASE_URL}/balances/history`;
      if (selectedTag) {
        url += `?tag=${encodeURIComponent(selectedTag)}`;
      }

      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data: ApiResponse = await response.json();

      setStrategies(data.strategies);

      // Calculate total balance from the last record of each strategy
      let total = 0;
      let previousDayTotal = 0;
      let latestTimestamp = 0;
      let hasData = false;
      let totalFlows = 0;

      const targetTime = getPreviousDay8AMUTC();

      data.strategies.forEach(strategyData => {
        if (strategyData.historicalBalances && strategyData.historicalBalances.length > 0) {
          const lastBalance = strategyData.historicalBalances[0]; // First element is the latest
          total += parseFloat(lastBalance.balance);

          if (lastBalance.timestamp > latestTimestamp) {
            latestTimestamp = lastBalance.timestamp;
          }

          // Calculate flows from userTransactions
          if (lastBalance.userTransactions && lastBalance.userTransactions.length > 0) {
            lastBalance.userTransactions.forEach((transaction: any) => {
              const transactionTime = new Date(transaction.timestamp).getTime();
              if (transactionTime >= targetTime) {
                const amount = parseFloat(transaction.amount);
                if (transaction.type === 'deposit') {
                  totalFlows += amount;
                } else if (transaction.type === 'withdrawal') {
                  totalFlows -= amount;
                }
              }
            });
          }

          // Find data from previous day at 8 AM UTC
          const previousDayData = findClosestDataPoint(strategyData.historicalBalances, targetTime);

          if (previousDayData) {
            previousDayTotal += parseFloat(previousDayData.balance);
            hasData = true;
          }
        }
      });

      setTotalBalance(total.toString());
      setPreviousDayBalance(previousDayTotal.toString());
      setHasPreviousDayData(hasData);
      setLastUpdated(latestTimestamp);

      // Calculate balance changes
      if (hasData && previousDayTotal > 0) {
        const totalChange = total - previousDayTotal;
        const totalChangePercentage = (totalChange / previousDayTotal) * 100;
        
        // Calculate pure balance change (excluding flows)
        const pureChange = totalChange - totalFlows;
        const pureChangePercentage = (pureChange / previousDayTotal) * 100;

        setBalanceChange(totalChange.toString());
        setBalanceChangePercentage(totalChangePercentage.toString());
        setPureBalanceChange(pureChange.toString());
        setPureBalanceChangePercentage(pureChangePercentage.toString());
        setTotalFlows(totalFlows.toString());
      } else {
        setBalanceChange('0');
        setBalanceChangePercentage('0');
        setPureBalanceChange('0');
        setPureBalanceChangePercentage('0');
        setTotalFlows('0');
      }

      setError(null);
    } catch (err) {
      setError('Failed to fetch strategies from API');
      console.error('Error fetching strategies:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchStrategies();
    fetchTags();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refetch strategies when tag changes
  useEffect(() => {
    if (!loading) { // Only refetch if not in initial loading state
      fetchStrategies();
    }
  }, [selectedTag]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRefresh = () => {
    fetchStrategies(true);
  };

  // Format total balance (assuming USDC with 6 decimals)
  const formatTotalBalance = (balance: string) => {
    const balanceInUsdc = parseFloat(balance);
    return balanceInUsdc.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  // Format balance change (assuming USDC with 6 decimals)
  const formatBalanceChange = (change: string) => {
    const changeInUsdc = parseFloat(change);
    const sign = changeInUsdc >= 0 ? '+' : '';
    return `${sign}${changeInUsdc.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;
  };

  // Format percentage change
  const formatPercentageChange = (percentage: string) => {
    const percentageValue = parseFloat(percentage);
    const sign = percentageValue >= 0 ? '+' : '';
    return `${sign}${percentageValue.toFixed(2)}%`;
  };

  // Format last updated time
  const formatLastUpdated = (timestamp: number) => {
    if (timestamp === 0) return 'Never';
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  if (loading) {
    return (
      <Container maxW="container.xl" py={8}>
        <VStack spacing={8} align="center" justify="center" minH="50vh">
          <Spinner size="xl" color="blue.500" />
          <Text fontSize="lg" color="gray.600">
            Loading strategies...
          </Text>
        </VStack>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxW="container.xl" py={8}>
        <Alert status="error" borderRadius="md">
          <AlertIcon />
          <VStack align="start" spacing={4} flex={1}>
            <Heading size="md" color="red.500">Error</Heading>
            <Text color="red.500">{error}</Text>
            <Button
              colorScheme="blue"
              onClick={handleRefresh}
              size="sm"
            >
              Retry
            </Button>
          </VStack>
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxW="container.xl" py={8}>
      <VStack spacing={8} align="stretch">
        {/* Dashboard Header */}
        <Box>
          <Heading size="xl" color="gray.800" mb={6} textAlign="center">
            SwissBorg Earn
          </Heading>
        </Box>

        {/* Tag Selector */}
        <TagSelector
          tags={tags}
          selectedTag={selectedTag}
          onTagChange={setSelectedTag}
          loading={tagsLoading}
        />

        {/* Total Balance Summary */}
        <Box
          bg="white"
          p={6}
          borderRadius="lg"
          boxShadow="sm"
          border="1px"
          borderColor="gray.200"
        >
          <VStack spacing={4} align="stretch">
            <HStack justify="space-between" align="center">
              <Stat>
                <StatLabel fontSize="lg" color="gray.600">Total Portfolio Balance</StatLabel>
                <StatNumber fontSize="3xl" color="green.500">
                  ${formatTotalBalance(totalBalance)} USDC
                </StatNumber>
                <StatHelpText color="gray.500">
                  Last refreshed: {formatLastUpdated(lastUpdated)}
                </StatHelpText>
              </Stat>
              <Tooltip label="Refresh data" placement="top">
                <IconButton
                  aria-label="Refresh data"
                  icon={<Text fontSize="lg">🔄</Text>}
                  onClick={handleRefresh}
                  isLoading={refreshing}
                  colorScheme="blue"
                  variant="outline"
                  size="lg"
                  _hover={{
                    bg: 'blue.50',
                    transform: 'rotate(180deg)',
                    transition: 'all 0.3s ease'
                  }}
                  transition="all 0.3s ease"
                />
              </Tooltip>
            </HStack>

            {/* Balance Change Section */}
            {hasPreviousDayData ? (
              <Box
                bg="gray.50"
                p={4}
                borderRadius="md"
                border="1px"
                borderColor="gray.200"
              >
                <VStack spacing={3} align="stretch">
                  <Text fontSize="sm" fontWeight="medium" color="gray.600">
                    Balance Change (since yesterday 8 AM UTC)
                  </Text>
                  
                  {/* Total Balance Change */}
                  <Box>
                    <HStack justify="space-between" align="center">
                      <Text fontSize="md" fontWeight="medium" color="gray.700">
                        Total Change:
                      </Text>
                      <Text fontSize="lg" fontWeight="semibold" color={parseFloat(balanceChange) >= 0 ? 'green.500' : 'red.500'}>
                        {formatBalanceChange(balanceChange)} USDC
                      </Text>
                    </HStack>
                    <HStack justify="end" spacing={4} align="center" mt={1}>
                      <Text fontSize="sm" color={parseFloat(balanceChangePercentage) >= 0 ? 'green.500' : 'red.500'}>
                        {formatPercentageChange(balanceChangePercentage)}
                      </Text>
                    </HStack>
                  </Box>
                  
                  {/* Pure Balance Change (excluding flows) */}
                  <Box>
                    <HStack justify="space-between" align="center">
                      <Text fontSize="md" fontWeight="medium" color="gray.700">
                        Pure Change (excl. flows):
                      </Text>
                      <Text fontSize="lg" fontWeight="semibold" color={parseFloat(pureBalanceChange) >= 0 ? 'green.500' : 'red.500'}>
                        {formatBalanceChange(pureBalanceChange)} USDC
                      </Text>
                    </HStack>
                    <HStack justify="end" spacing={4} align="center" mt={1}>
                      <Text fontSize="sm" color={parseFloat(pureBalanceChangePercentage) >= 0 ? 'green.500' : 'red.500'}>
                        {formatPercentageChange(pureBalanceChangePercentage)}
                      </Text>
                      <Text fontSize="sm" color="blue.500" fontWeight="medium">
                        APY: {calculateAPY(pureBalanceChange, previousDayBalance)}%
                      </Text>
                    </HStack>
                  </Box>
                  
                  {/* Net Flows */}
                  {parseFloat(totalFlows) !== 0 && (
                    <Box pt={2} borderTop="1px" borderColor="gray.200">
                      <HStack justify="space-between" align="center">
                        <Text fontSize="sm" fontWeight="medium" color="gray.600">
                          Net Flows:
                        </Text>
                        <Text fontSize="sm" fontWeight="semibold" color={parseFloat(totalFlows) >= 0 ? 'blue.500' : 'orange.500'}>
                          {formatBalanceChange(totalFlows)} USDC
                        </Text>
                      </HStack>
                    </Box>
                  )}
                </VStack>
              </Box>
            ) : (
              <Box
                bg="yellow.50"
                p={4}
                borderRadius="md"
                border="1px"
                borderColor="yellow.200"
              >
                <HStack spacing={2} align="center">
                  <Text fontSize="sm" color="yellow.700">
                    ⚠️ No data available from yesterday 8 AM UTC (±5 min)
                  </Text>
                </HStack>
              </Box>
            )}
          </VStack>
        </Box>

        {/* Strategies Grid */}
        {strategies.length > 0 ? (
          <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
            {strategies.map((strategyData) => (
              <StrategyCard
                key={strategyData.strategy.id}
                strategyData={strategyData}
              />
            ))}
          </SimpleGrid>
        ) : (
          <Box
            textAlign="center"
            py={12}
            bg="white"
            borderRadius="lg"
            boxShadow="sm"
            border="1px"
            borderColor="gray.200"
          >
            <Text fontSize="lg" color="gray.500">
              No strategies found.
            </Text>
          </Box>
        )}
      </VStack>
    </Container>
  );
};

export default Dashboard;
